interface LandingPageProps {
  onEnter: () => void;
  preloaded: boolean;
  trackCount?: number;
}

/** Particles — rising dots (CSS-only, no JS overhead) */
function Particles() {
  const dots = Array.from({ length: 18 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 6}s`,
    duration: `${6 + Math.random() * 8}s`,
    size: `${2 + Math.random() * 3}px`,
    color: Math.random() > 0.5 ? '#ffd93d' : '#ff6b4a',
    opacity: 0.15 + Math.random() * 0.25,
  }));

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {dots.map((d) => (
        <div
          key={d.id}
          className="absolute rounded-full animate-rise"
          style={{
            left: d.left,
            bottom: '-10px',
            width: d.size,
            height: d.size,
            backgroundColor: d.color,
            opacity: d.opacity,
            animationDelay: d.delay,
            animationDuration: d.duration,
          }}
        />
      ))}
    </div>
  );
}

/** Pulsing waveform bars — simulates an audio visualizer */
function WaveformBars() {
  const bars = [
    { h: 12, delay: '0s' },
    { h: 24, delay: '0.15s' },
    { h: 8, delay: '0.3s' },
    { h: 18, delay: '0.45s' },
    { h: 10, delay: '0.6s' },
  ];

  return (
    <div className="flex items-end gap-1.5 h-8 mt-2">
      {bars.map((b, i) => (
        <div
          key={i}
          className="w-1.5 rounded-full bg-[#ffd93d]/40 animate-waveform"
          style={{
            height: `${b.h}px`,
            animationDelay: b.delay,
          }}
        />
      ))}
    </div>
  );
}

/**
 * LandingPage — shown before the 3D game.
 * Sunset festival aesthetic with particles, wordmark, CTA button,
 * pulsing waveform, and track count badge.
 */
export default function LandingPage({ onEnter, preloaded, trackCount }: LandingPageProps) {
  return (
    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-b from-[#0d0d1a] to-[#1a1025] relative overflow-hidden">
      {/* Rising particles */}
      <Particles />

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center gap-6 px-6 animate-fade-in">
        {/* Wordmark */}
        <h1
          className="font-['Outfit'] font-extrabold text-4xl sm:text-5xl md:text-6xl text-white tracking-tight text-center leading-none"
          style={{
            textShadow:
              '0 0 40px rgba(255,217,61,0.3), 0 0 80px rgba(255,107,74,0.15)',
          }}
        >
          DISCOVER
          <br />
          HOUSE MUSIC
        </h1>

        {/* Tagline */}
        <p className="font-['Inter'] text-base sm:text-lg text-[#a0a0c0] text-center max-w-sm leading-relaxed">
          walk the festival. find the sound.
          <br />
          new city every friday.
        </p>

        {/* CTA Button */}
        <button
          onClick={onEnter}
          className={`
            mt-4 px-10 py-4 rounded-full font-['Outfit'] font-bold text-lg sm:text-xl
            bg-gradient-to-r from-[#ff6b4a] to-[#ff8c6a]
            text-[#ffd93d]
            transition-all duration-300 ease-out
            hover:scale-105 hover:shadow-[0_0_30px_rgba(255,107,74,0.4)]
            active:scale-95
            ${preloaded
              ? 'ring-2 ring-[#ffd93d]/40 animate-pulse-ring'
              : 'animate-shimmer'
            }
          `}
        >
          🔊&nbsp; DISCOVER
        </button>

        {/* Pulsing waveform bars */}
        <WaveformBars />

        {/* Track count badge */}
        {trackCount != null && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
            <span className="text-sm">🎵</span>
            <span className="font-['Inter'] text-xs text-[#a0a0c0]">
              {trackCount} stage{trackCount !== 1 ? 's' : ''} this week
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
